import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import { useShowToast } from '@/design_system/Toast';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconError from '@/icons/Error.svg';
import { useAcceptDispatch, useRefuseDispatch, useSubmitDispatchProposal } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { areArticleSnashotsEqual } from '@/utils/articleSnapshot';

import { ArticleActionProps } from './ArticleActions';

export const AcceptDispatchAction = ({ onActionDone }: ArticleActionProps) => {
  const { t } = useLingui();
  const { request, view: requestView } = useRequestContext();
  const { article, state, hasError, view } = useArticleContext();

  const [isOpenRefuseDialog, setIsOpenRefuseDialog] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    mutateAsync: acceptDispatch,
    isPending: isAcceptDispatchPending,
    isSuccess: isAcceptDispatchSuccess,
  } = useAcceptDispatch({
    articleId: article.id,
  });

  const {
    mutateAsync: submitDispatchProposal,
    isPending: isSubmitDispatchProposalPending,
    isSuccess: isSubmitDispatchProposalSuccess,
  } = useSubmitDispatchProposal({
    articleId: article.id,
  });

  const changesWereMade = !areArticleSnashotsEqual(article);

  const handleAcceptOrSubmitProposal = async () => {
    const { hasError } = state.errors.check({
      services: {
        actions: {
          actions: {
            missingWorkshopPrice:
              requestView.workshopPrice.shown &&
              view.services.actions.fields.workshopPrice.editable &&
              view.services.actions.fields.workshopPrice.required,
            workshopPriceUpdateInProgress:
              requestView.workshopPrice.shown &&
              view.services.actions.fields.workshopPrice.editable,
            organizationPriceUpdateInProgress:
              requestView.organizationPrice.shown &&
              view.services.actions.fields.organizationPrice.editable,
            spareParts: view.services.actions.fields.spareParts.shown
              ? {
                  missingWorkshopPrice:
                    requestView.workshopPrice.shown &&
                    view.services.actions.fields.spareParts.workshopPrice.editable &&
                    view.services.actions.fields.spareParts.workshopPrice.required,
                  workshopPriceUpdateInProgress:
                    requestView.workshopPrice.shown &&
                    view.services.actions.fields.spareParts.workshopPrice.editable,
                  organizationPriceUpdateInProgress:
                    requestView.organizationPrice.shown &&
                    view.services.actions.fields.spareParts.organizationPrice.editable,
                }
              : undefined,
          },
        },
      },
    });

    setApiError(null);

    if (!hasError) {
      try {
        if (changesWereMade) {
          await submitDispatchProposal();
        } else {
          await acceptDispatch();
        }

        onActionDone();
      } catch (err: any) {
        console.error(err);
        setApiError(
          (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
        );
      }
    }
  };

  const autoAcceptDispatchProposal =
    !!article.workshopOrganization?.workshopConfig?.autoAcceptDispatchProposal;

  return (
    <>
      <Footer>
        {(hasError || apiError) && (
          <FooterError>
            {hasError ? (
              <Trans id="article.actions.accept_dispatch.errors">
                Please fill the missing information in order to accept the job
              </Trans>
            ) : (
              apiError
            )}
          </FooterError>
        )}
        <FooterButtons>
          <Button
            size="medium"
            variant="secondary-danger"
            onPress={() => setIsOpenRefuseDialog(true)}
          >
            <IconError />
            <Trans id="article.actions.accept_dispatch.refuse">Refuse the job</Trans>
          </Button>
          <Button
            variant="primary"
            size="medium"
            onPress={handleAcceptOrSubmitProposal}
            isLoading={
              isAcceptDispatchPending ||
              isAcceptDispatchSuccess ||
              isSubmitDispatchProposalPending ||
              isSubmitDispatchProposalSuccess
            }
          >
            {changesWereMade && !autoAcceptDispatchProposal ? (
              <Trans id="article.actions.accept_dispatch.proposal">Send new quote proposal</Trans>
            ) : (
              <Trans id="article.actions.accept_dispatch.accept">Accept the job</Trans>
            )}
          </Button>
        </FooterButtons>
      </Footer>
      <RefuseDispatchDialog
        isOpen={isOpenRefuseDialog}
        setIsOpen={setIsOpenRefuseDialog}
        // If the workshop is refusing the only item of the request, after the refusal it won't
        // be able to see the request anymore.
        onActionDone={() => onActionDone({ navigateToRequests: request.articles.length === 1 })}
      />
    </>
  );
};

const RefuseDispatchDialog = ({
  isOpen,
  setIsOpen,
  onActionDone,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onActionDone: () => void;
}) => {
  const { t } = useLingui();
  const showToast = useShowToast();
  const { article, iam } = useArticleContext();

  const {
    mutateAsync: refuseDispatch,
    isPending: isRefuseDispatchPending,
    isSuccess: isRefuseDispatchSuccess,
  } = useRefuseDispatch({
    article,
  });

  const [showErrors, setShowErrors] = useState(false);

  const [reason, setReason] = useState('');

  const save = async () => {
    setShowErrors(true);

    if (!reason) {
      return;
    }

    await refuseDispatch({ refusalReason: reason });

    showToast({
      type: 'success',
      text: t({
        id: 'dispatch-refusal-modal.actions.refuse.success',
        message: 'The item has been successfully refused',
      }),
    });

    onActionDone();
  };

  return (
    <Dialog
      title={t({ id: 'dispatch-refusal-modal.title', message: 'Refuse the job' })}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <main>
        <Stack gap="1rem">
          <Paragraph>
            {iam.hasOnlyWorkshopScopeOnArticle ? (
              <Trans id="dispatch-refusal-modal.text">
                Once you refuse the job, the item won't appear anymore on your requests dashboard.
                Please specify the reason for your refusal.
              </Trans>
            ) : iam.hasUserScopeOnArticle('workshop') ? (
              <Trans id="dispatch-refusal-modal.text.reason">
                Please specify the reason for your refusal.
              </Trans>
            ) : (
              <Trans id="dispatch-refusal-modal.on-behalf.text">
                Please specify the reason for the workshop's refusal.
              </Trans>
            )}
          </Paragraph>
          <TextArea
            ariaLabel={t({
              id: 'dispatch-refusal-modal.reason.label',
              message: 'Refusal reason',
            })}
            placeholder={t({
              id: 'dispatch-refusal-modal.reason.placeholder',
              message: 'Write refusal reason',
            })}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            isInvalid={showErrors && !reason}
          />
        </Stack>
      </main>
      <Footer>
        <FooterButtons>
          <Button variant="secondary" size="medium" onPress={() => setIsOpen(false)}>
            <Trans id="dispatch-refusal-modal.actions.cancel">Cancel</Trans>
          </Button>
          <Button
            variant="danger"
            size="medium"
            onPress={save}
            isLoading={isRefuseDispatchPending || isRefuseDispatchSuccess}
          >
            <Trans id="dispatch-refusal-modal.actions.refuse">Refuse the job</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </Dialog>
  );
};
